import { useAuthSession } from "../hooks/useWorkspaceRole.js";

/**
 * Humanize a canonical tool name to a present-continuous activity label.
 * `yagni_workspace_search` → "Searching workspace". Used as a fallback
 * when the tool didn't ship an explicit `actionLabel`.
 *
 * Algorithm: drop the `yagni_` prefix; identify the verb (last
 * underscore-delimited segment, possibly camelCased — `createEvent`
 * → verb=create, object=event); apply the -ing rule to the verb and
 * append the remaining segments as the object phrase.
 *
 * Edge cases the fallback intentionally accepts:
 *   - `yagni_gmail_send` → "Sending gmail" (object=gmail; technically
 *     awkward but recognizable; tools that read poorly should ship an
 *     explicit `actionLabel`).
 *   - Single-segment trailing names like `yagni_search` → "Searching".
 */
export function humanizeToolNameAsGerund(name: string): string {
  const stripped = name.replace(/^yagni_/, "");
  const parts = stripped.split("_").filter((p) => p.length > 0);
  if (parts.length === 0) return "Working";
  const tail = parts[parts.length - 1] ?? "";
  // Split camelCase: createEvent → ["create", "Event"]
  const tailWords = tail
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .toLowerCase()
    .split(" ")
    .filter((w) => w.length > 0);
  const verb = tailWords[0] ?? tail;
  const tailObject = tailWords.slice(1).join(" ");
  const objectPhrase = [
    ...parts.slice(0, -1),
    ...(tailObject ? [tailObject] : []),
  ].join(" ");

  const gerund = toGerund(verb);
  if (!gerund) return "Working";
  if (objectPhrase) return `${capitalize(gerund)} ${objectPhrase}`;
  return capitalize(gerund);
}

function toGerund(verb: string): string {
  if (!verb) return "";
  // Common irregulars first.
  const irregulars: Record<string, string> = {
    get: "getting",
    set: "setting",
    run: "running",
    sit: "sitting",
    log: "logging",
    plan: "planning",
    forget: "forgetting",
  };
  if (irregulars[verb]) return irregulars[verb];
  // Standard rules.
  if (verb.endsWith("ie")) return verb.slice(0, -2) + "ying";
  if (verb.endsWith("e") && !verb.endsWith("ee")) return verb.slice(0, -1) + "ing";
  return verb + "ing";
}

function capitalize(s: string): string {
  return s.length === 0 ? s : s.charAt(0).toUpperCase() + s.slice(1);
}

/**
 * React hook that returns the activity label for a tool. Reads
 * `session.toolActionLabels` populated at session bootstrap; falls
 * back to the humanizer for tools the registry didn't ship a label for.
 */
export function useToolActionLabel(toolName: string): string {
  const fromSession = useAuthSession()?.toolActionLabels?.[toolName];
  if (fromSession && fromSession.length > 0) return fromSession;
  return humanizeToolNameAsGerund(toolName);
}
